/**
 * The Devices title row's global action: the switch the open tab depends on
 * (Akash, 2026-11-02: the title row follows the tab). Robot lift rides on
 * Lifts, Door control on Doors, nothing on Edge box.
 *
 * It only reports the verdict and opens the full panel in a drawer. The
 * panels stay the one place that arms or disarms, so the reason box, the
 * stranded-robot acknowledgement and the "acknowledged, not confirmed"
 * sentences are not repeated here. Disarm is never harder than arm
 * (invariant I2): an armed gate always offers it, whatever else is wrong.
 */
import { useRef } from 'react';
import { useDoorWriteGates } from '../../api/hooks/useDoors';
import { useDeviceNotes } from '../../api/hooks/useDeviceNotes';
import { useLiftWriteGate } from '../../api/hooks/useLifts';
import { useAuth } from '../../auth/useAuth';
import { useMinuteTick } from '../../state/clock';
import { useActiveSite } from '../../state/useActiveSite';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { formatClock } from './lift-reading';
import { DOOR_ARM_WRITES_ROLES, LIFT_ARM_WRITES_ROLES, type RoleToken } from '../../auth/types';
import { StatusChip } from '../../design-system/components/StatusChip';
import type { DoorWriteGate } from '../../api/v1-client';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import {
  armBlockedReason as doorArmBlockedReason,
  doorGateStatus,
  UNKNOWN,
  type DoorGateStatus,
} from './door-gate-status';
import { DoorWriteGatePanel } from './DoorWriteGatePanel';
import { armBlockedReason, writeGateStatus } from './lift-dispatch-status';
import { LiftWriteGatePanel } from './LiftWriteGatePanel';
import './DeviceDetailDrawers.css';

export type GateKind = 'lift' | 'door';

const GATE_TITLE: Readonly<Record<GateKind, string>> = {
  lift: 'Robot lift rides',
  door: 'Door control',
};

function mayArm(roles: readonly RoleToken[], allowed: readonly RoleToken[]): boolean {
  return roles.some((role) => allowed.includes(role));
}

/** The label for the one button: what a press leads to, for this reader. */
function gateAction(allowed: boolean, armed: boolean, canArm: boolean): string {
  if (!allowed) return 'Details';
  if (armed) return 'Disarm…';
  return canArm ? 'Arm…' : 'Details';
}

/** Worst first, so the chip never reads better than any one door system. */
const DOOR_GATE_RANK: Readonly<Record<DoorGateStatus['kind'], number>> = {
  unvalidated: 0,
  disarmed: 1,
  shadow: 2,
  unknown: 3,
  unsupported: 4,
  no_adapter: 5,
  armed: 6,
};

function worstDoorGate(gates: readonly DoorWriteGate[]): DoorGateStatus {
  return gates
    .map(doorGateStatus)
    .reduce((worst, s) => (DOOR_GATE_RANK[s.kind] < DOOR_GATE_RANK[worst.kind] ? s : worst));
}

function GateControl({
  kind,
  chip,
  label,
  small,
  action,
  onOpen,
}: {
  kind: GateKind;
  chip: DoorGateStatus['chip'];
  label: string;
  small?: string | null;
  action: string;
  onOpen: () => void;
}) {
  return (
    <span className="nx-devices__gate" data-testid={`${kind}-gate-control`}>
      <span className="nx-devices__gate-title">{GATE_TITLE[kind]}</span>
      <span data-testid={`${kind}-gate-chip`}>
        <StatusChip status={chip} label={label} />
      </span>
      {small && <span className="nx-cell-small">{small}</span>}
      <button
        type="button"
        className="nx-screen-btn"
        onClick={onOpen}
        data-testid={`${kind}-gate-open`}
      >
        {action}
      </button>
    </span>
  );
}

/** "by R. Okafor, 09:10". Only beside an armed gate: the note says who last
 *  armed it from the console, and the live gate says whether it still is. */
function armedWords(note: { by: string | null; at_ms: number } | null | undefined, clock: (ms: number) => string) {
  if (!note) return null;
  return note.by ? `by ${note.by}, ${clock(note.at_ms)}` : `since ${clock(note.at_ms)}`;
}

function LiftGateControl({ onOpen }: { onOpen: () => void }) {
  const { roles } = useAuth();
  const gate = useLiftWriteGate().data;
  const status = writeGateStatus(gate);
  const notes = useDeviceNotes().data;
  const { site, sites } = useActiveSite();
  const zone = resolveSiteTimeZone(sites, site?.id);
  const wallNowMs = useMinuteTick();
  const clock = (ms: number) => formatClock(ms, zone.source === 'site' ? zone.timeZone : null, wallNowMs);
  return (
    <GateControl
      kind="lift"
      chip={status.chip}
      label={status.label}
      small={status.kind === 'armed' ? armedWords(notes?.lift_gate, clock) : null}
      action={gateAction(
        mayArm(roles, LIFT_ARM_WRITES_ROLES),
        gate?.writes_armed === true,
        armBlockedReason(status) === undefined,
      )}
      onOpen={onOpen}
    />
  );
}

function DoorGateControl({ onOpen }: { onOpen: () => void }) {
  const { roles } = useAuth();
  const gates = useDoorWriteGates().data;
  // A site with no gated door system has no such switch: not a fault, and
  // nothing to put in a title row.
  if (gates === null || (gates !== undefined && gates.length === 0)) return null;
  const status = gates === undefined ? UNKNOWN : worstDoorGate(gates);
  const armedBy = gates?.find((g) => g.armed && g.armed_by)?.armed_by;
  const anyArmed = gates?.some((g) => g.armed) === true;
  const canArm = gates?.some((g) => doorArmBlockedReason(doorGateStatus(g)) === undefined) === true;
  return (
    <GateControl
      kind="door"
      chip={status.chip}
      label={status.label}
      small={status.kind === 'armed' && armedBy ? `by ${armedBy}` : null}
      action={gateAction(mayArm(roles, DOOR_ARM_WRITES_ROLES), anyArmed && !canArm, canArm)}
      onOpen={onOpen}
    />
  );
}

export function DevicesGateControls({
  kind,
  onOpen,
}: {
  kind: GateKind | null;
  onOpen: (kind: GateKind) => void;
}) {
  if (kind === 'lift') return <LiftGateControl onOpen={() => onOpen('lift')} />;
  if (kind === 'door') return <DoorGateControl onOpen={() => onOpen('door')} />;
  return null;
}

/** The full panel, as the lift and door drawers show it. */
export function GateDrawer({ kind, onClose }: { kind: GateKind; onClose: () => void }) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);
  return (
    <aside
      ref={panelRef}
      className="nx-device-drawer"
      role="dialog"
      aria-label={GATE_TITLE[kind]}
      data-testid={`${kind}-gate-drawer`}
    >
      <header className="nx-device-drawer__head">
        <h2>{GATE_TITLE[kind]}</h2>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="nx-device-drawer__body">
        {kind === 'lift' ? <LiftWriteGatePanel /> : <DoorWriteGatePanel />}
      </div>
    </aside>
  );
}
